import { useState, useEffect, useContext } from "react"
import { GlobalContextProvider } from "./functions/GlobalContext"
import {
    Box, Text, HStack, VStack, Center, Button, Input, Select, Flex, SimpleGrid,
    Table, Thead, Tbody, Tr, Th, Td, TableContainer,
    Spinner, Icon, Breadcrumb, BreadcrumbItem, BreadcrumbLink,
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
    useDisclosure, AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader,
    AlertDialogContent, AlertDialogOverlay, Badge,
} from "@chakra-ui/react"
import { AiFillPlusCircle, AiFillEdit, AiFillDelete, AiFillBank, AiOutlineMail, AiOutlinePhone, AiOutlineUser, AiOutlineCalendar, AiOutlineEnvironment } from 'react-icons/ai'
import { ChevronRightIcon } from '@chakra-ui/icons'
import InfoModal from "./modals/InfoModal"
import Const from "../Consts"
import { Twirp, TwirpProps } from "./functions/Twirp"
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

// Level: 0=Country, 1=State, 2=City, 3=Cabang, 4=Ranting
const LEVEL_NAMES = ["Country", "State", "City", "Cabang", "Ranting"]

type OrgItem = {
    id: number
    nama: string
    status: number
    email: string
    telepon: string
    jadwal?: string
    alamat?: string
    id_penanggung_jawab: number
}

type FormData = {
    nama: string
    status: number
    email: string
    telepon: string
    jadwal: string
    alamat: string
    id_penanggung_jawab: number
}

type PJUser = {
    id: number
    nama: string
    username: string
}

const emptyForm: FormData = { nama: "", status: 1, email: "", telepon: "", jadwal: "", alamat: "", id_penanggung_jawab: 0 }

export default function OrgManager() {
    const globalContext = useContext(GlobalContextProvider)
    const { t } = useTranslation();
    const navigate = useNavigate()

    const [level, setLevel] = useState(0)
    const [items, setItems] = useState<OrgItem[]>([])
    const [loading, setLoading] = useState(false)

    // Drill-down context: which parent is selected at each level
    const [countryId, setCountryId] = useState(0)
    const [countryName, setCountryName] = useState("")
    const [stateId, setStateId] = useState(0)
    const [stateName, setStateName] = useState("")
    const [cityId, setCityId] = useState(0)
    const [cityName, setCityName] = useState("")
    const [cabangId, setCabangId] = useState(0)
    const [cabangName, setCabangName] = useState("")

    // Selected node per level (for the parent info card) + resolved PJ labels
    const [parents, setParents] = useState<(OrgItem | null)[]>([null, null, null, null])
    const [parentPJ, setParentPJ] = useState<{ [key: number]: string }>({})

    // Form modal
    const { isOpen: formIsOpen, onOpen: formOnOpen, onClose: formOnClose } = useDisclosure()
    const [editingId, setEditingId] = useState(0)
    const [formData, setFormData] = useState<FormData>(emptyForm)

    // Delete confirmation
    const [deleteTarget, setDeleteTarget] = useState<OrgItem | null>(null)
    const [deleting, setDeleting] = useState(false)
    const cancelRef = { current: null as any }

    // Info modal
    const [modalData, setModalData] = useState({ title: "", main: "", isOpen: false })
    const closeModal = () => setModalData({ ...modalData, isOpen: false })

    // Ranting detail modal (leaf level has no children to drill into)
    const [viewItem, setViewItem] = useState<OrgItem | null>(null)

    // Penanggung jawab picker
    const [pjSelected, setPjSelected] = useState<PJUser | null>(null)
    const [pjSearch, setPjSearch] = useState("")
    const [pjResults, setPjResults] = useState<PJUser[]>([])

    const token = globalContext?.GetLoginData().token
    const isAdmin = globalContext?.GetLoginData().isAdmin || false
    const userId = +(globalContext?.GetLoginData().userId || 0)

    const canEditItem = (item: OrgItem) => isAdmin || item.id_penanggung_jawab === userId

    const callApi = async (url: string, data: any): Promise<{ state: number, data: any, axiosError: any }> => {
        const props: TwirpProps = {
            url, headers: { Authorization: token }, timeout: 10000, data
        }
        return await Twirp(props)
    }

    const handleApiError = (result: { state: number, axiosError: any }) => {
        if (result.state == 2) {
            setModalData({ ...modalData, isOpen: true, title: result.axiosError.code, main: result.axiosError.message })
        } else if (result.state == 1) {
            let msg = result.axiosError.response?.data?.msg || result.axiosError.message
            setModalData({ ...modalData, isOpen: true, title: result.axiosError.code, main: msg })
            if (result.axiosError.response?.status == 401) {
                navigate('/logout')
            }
        }
    }

    const loadItems = async () => {
        setLoading(true)
        let url = ""
        let data: any = {}

        if (level == 0) {
            url = Const.API_ListCountries
            data = {}
        } else if (level == 1) {
            url = Const.API_ListStates
            data = { id: countryId }
        } else if (level == 2) {
            url = Const.API_ListCities
            data = { id: stateId }
        } else if (level == 3) {
            url = Const.API_ListDistrict
            data = { id: cityId }
        } else if (level == 4) {
            url = Const.API_ListRanting
            data = { id: cabangId }
        }

        const result = await callApi(url, data)
        if (result.state == 0) {
            let raw: any[] = []
            if (level == 4) {
                raw = result.data.Rantings || []
            } else {
                raw = result.data.Commons || []
            }
            setItems(raw.map((v: any) => ({
                id: +v.id,
                nama: v.nama,
                status: +v.status,
                email: v.email || "",
                telepon: v.telepon || "",
                jadwal: v.jadwal || "",
                alamat: v.alamat || "",
                id_penanggung_jawab: +v.id_penanggung_jawab || 0,
            })))
        } else {
            handleApiError(result)
            setItems([])
        }
        setLoading(false)
    }

    useEffect(() => {
        loadItems()
    }, [level, countryId, stateId, cityId, cabangId])

    const drillDown = (item: OrgItem) => {
        const p = [...parents]
        p[level] = item
        setParents(p)
        if (item.id_penanggung_jawab > 0 && !parentPJ[item.id]) {
            resolvePJLabel(item.id, item.id_penanggung_jawab)
        }
        if (level == 0) {
            setCountryId(item.id)
            setCountryName(item.nama)
            setLevel(1)
        } else if (level == 1) {
            setStateId(item.id)
            setStateName(item.nama)
            setLevel(2)
        } else if (level == 2) {
            setCityId(item.id)
            setCityName(item.nama)
            setLevel(3)
        } else if (level == 3) {
            setCabangId(item.id)
            setCabangName(item.nama)
            setLevel(4)
        }
    }

    const goBackTo = (targetLevel: number) => {
        setLevel(targetLevel)
    }

    const onView = (item: OrgItem) => {
        if (item.id_penanggung_jawab > 0 && !parentPJ[item.id]) {
            resolvePJLabel(item.id, item.id_penanggung_jawab)
        }
        setViewItem(item)
    }

    const resolvePJLabel = async (itemId: number, pjId: number) => {
        const result = await callApi(Const.API_ReadPesilat, { id: pjId })
        if (result.state == 0) {
            setParentPJ((prev) => ({ ...prev, [itemId]: `${result.data.nama} (${result.data.username})` }))
        }
    }

    const resetPJ = () => {
        setPjSelected(null)
        setPjSearch("")
        setPjResults([])
    }

    const loadPJName = async (id: number) => {
        const result = await callApi(Const.API_ReadPesilat, { id: id })
        if (result.state == 0) {
            setPjSelected({ id: id, nama: result.data.nama, username: result.data.username })
        }
    }

    const searchPJ = async (query: string) => {
        if (query.trim().length < 2) {
            setPjResults([])
            return
        }
        const result = await callApi(Const.API_SearchPesilat, { nama: query })
        if (result.state == 0) {
            const list = result.data.Pesilats || []
            setPjResults(list.slice(0, 20).map((v: any) => ({ id: +v.id, nama: v.nama, username: v.username })))
        }
    }

    const selectPJ = (p: PJUser) => {
        setPjSelected(p)
        setFormData({ ...formData, id_penanggung_jawab: p.id })
        setPjSearch("")
        setPjResults([])
    }

    const onAdd = () => {
        setEditingId(0)
        setFormData(emptyForm)
        resetPJ()
        formOnOpen()
    }

    const onEdit = (item: OrgItem) => {
        setEditingId(item.id)
        setFormData({
            nama: item.nama,
            status: item.status,
            email: item.email,
            telepon: item.telepon,
            jadwal: item.jadwal || "",
            alamat: item.alamat || "",
            id_penanggung_jawab: item.id_penanggung_jawab,
        })
        resetPJ()
        if (item.id_penanggung_jawab > 0) {
            loadPJName(item.id_penanggung_jawab)
        }
        formOnOpen()
    }

    const onDelete = (item: OrgItem) => {
        setDeleteTarget(item)
    }

    const confirmDelete = async () => {
        if (!deleteTarget) return
        setDeleting(true)
        let url = ""
        if (level == 0) url = Const.API_DeleteCountry
        else if (level == 1) url = Const.API_DeleteState
        else if (level == 2) url = Const.API_DeleteCity
        else if (level == 3) url = Const.API_DeleteCabang
        else if (level == 4) url = Const.API_DeleteRanting

        const result = await callApi(url, { id: deleteTarget.id })
        if (result.state == 0) {
            setDeleteTarget(null)
            loadItems()
        } else {
            handleApiError(result)
        }
        setDeleting(false)
    }

    const submitForm = async () => {
        if (formData.nama.trim() === "") {
            setModalData({ ...modalData, isOpen: true, title: t('Please check your data'), main: t('missing_fullname') })
            return
        }

        let url = ""
        let data: any = {}

        if (level == 0) {
            if (editingId > 0) {
                url = Const.API_UpdateCountry
                data = { nama: formData.nama, status: formData.status, email: formData.email, telepon: formData.telepon, id_penanggung_jawab: formData.id_penanggung_jawab, self_id: editingId }
            } else {
                url = Const.API_CreateCountry
                data = { nama: formData.nama, status: formData.status, email: formData.email, telepon: formData.telepon, id_penanggung_jawab: formData.id_penanggung_jawab }
            }
        } else if (level == 1) {
            if (editingId > 0) {
                url = Const.API_UpdateState
                data = { nama: formData.nama, status: formData.status, email: formData.email, telepon: formData.telepon, id_penanggung_jawab: formData.id_penanggung_jawab, self_id: editingId }
            } else {
                url = Const.API_CreateState
                data = { nama: formData.nama, status: formData.status, email: formData.email, telepon: formData.telepon, id_penanggung_jawab: formData.id_penanggung_jawab, parent_id: countryId }
            }
        } else if (level == 2) {
            if (editingId > 0) {
                url = Const.API_UpdateCity
                data = { nama: formData.nama, status: formData.status, email: formData.email, telepon: formData.telepon, id_penanggung_jawab: formData.id_penanggung_jawab, self_id: editingId }
            } else {
                url = Const.API_CreateCity
                data = { nama: formData.nama, status: formData.status, email: formData.email, telepon: formData.telepon, id_penanggung_jawab: formData.id_penanggung_jawab, parent_id: stateId }
            }
        } else if (level == 3) {
            if (editingId > 0) {
                url = Const.API_UpdateCabang
                data = { nama: formData.nama, status: formData.status, email: formData.email, telepon: formData.telepon, id_penanggung_jawab: formData.id_penanggung_jawab, self_id: editingId }
            } else {
                url = Const.API_CreateCabang
                data = { nama: formData.nama, status: formData.status, email: formData.email, telepon: formData.telepon, id_penanggung_jawab: formData.id_penanggung_jawab, parent_id: cityId }
            }
        } else if (level == 4) {
            if (editingId > 0) {
                url = Const.API_UpdateRanting
                data = { nama: formData.nama, status: formData.status, email: formData.email, telepon: formData.telepon, id_penanggung_jawab: formData.id_penanggung_jawab, jadwal: formData.jadwal, alamat: formData.alamat, ranting_id: editingId }
            } else {
                url = Const.API_CreateRanting
                data = { nama: formData.nama, status: formData.status, email: formData.email, telepon: formData.telepon, id_penanggung_jawab: formData.id_penanggung_jawab, jadwal: formData.jadwal, alamat: formData.alamat, cabang_id: cabangId }
            }
        }

        const result = await callApi(url, data)
        if (result.state == 0) {
            formOnClose()
            loadItems()
        } else {
            handleApiError(result)
        }
    }

    const currentLevelName = t(LEVEL_NAMES[level])
    const canDrillDown = level < 4

    return (
        <Box py={"10px"} px={"10px"} borderWidth='0px' borderRadius='lg' overflow='hidden'>
            <InfoModal isOpen={modalData.isOpen} title={modalData.title} mainText={modalData.main} closeFunction={closeModal} />

            {/* Breadcrumb */}
            <Breadcrumb spacing="8px" separator={<ChevronRightIcon color="gray.500" />} mb={"10px"}>
                <BreadcrumbItem>
                    <BreadcrumbLink onClick={() => goBackTo(0)} color={level == 0 ? "blue.500" : "gray.500"}>{t('Country')}</BreadcrumbLink>
                </BreadcrumbItem>
                {level >= 1 ? (
                    <BreadcrumbItem>
                        <BreadcrumbLink onClick={() => goBackTo(1)} color={level == 1 ? "blue.500" : "gray.500"}>{countryName}</BreadcrumbLink>
                    </BreadcrumbItem>
                ) : ''}
                {level >= 2 ? (
                    <BreadcrumbItem>
                        <BreadcrumbLink onClick={() => goBackTo(2)} color={level == 2 ? "blue.500" : "gray.500"}>{stateName}</BreadcrumbLink>
                    </BreadcrumbItem>
                ) : ''}
                {level >= 3 ? (
                    <BreadcrumbItem>
                        <BreadcrumbLink onClick={() => goBackTo(3)} color={level == 3 ? "blue.500" : "gray.500"}>{cityName}</BreadcrumbLink>
                    </BreadcrumbItem>
                ) : ''}
                {level >= 4 ? (
                    <BreadcrumbItem>
                        <BreadcrumbLink color="blue.500">{cabangName}</BreadcrumbLink>
                    </BreadcrumbItem>
                ) : ''}
            </Breadcrumb>

            {/* Selected parent node info */}
            {level > 0 && parents[level - 1] ? (
                <Box mb={"10px"} borderWidth='1px' borderColor='gray.200' borderRadius='lg' overflow='hidden' boxShadow='sm'>
                    <Flex bg='blue.50' px={"12px"} py={"8px"} align='center' justify='space-between' borderBottomWidth='1px' borderColor='blue.100'>
                        <HStack spacing={2}>
                            <Icon as={AiFillBank} color='blue.500' boxSize={5} />
                            <Text fontSize='md' fontWeight='bold' color='gray.700'>{parents[level - 1]!.nama}</Text>
                            <Badge colorScheme='blue' variant='subtle'>{t(LEVEL_NAMES[level - 1])}</Badge>
                        </HStack>
                        <Badge colorScheme={parents[level - 1]!.status > 0 ? 'green' : 'gray'} variant='solid'>
                            {parents[level - 1]!.status > 0 ? t('active') : t('inactive')}
                        </Badge>
                    </Flex>
                    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3} px={"12px"} py={"10px"}>
                        <HStack spacing={2} align='start'>
                            <Icon as={AiOutlineMail} color='blue.400' boxSize={4} mt="2px" />
                            <VStack align='start' spacing={0}>
                                <Text fontSize='xs' color='gray.500'>{t('E-mail')}</Text>
                                <Text fontSize='sm' fontWeight='medium'>{parents[level - 1]!.email || '-'}</Text>
                            </VStack>
                        </HStack>
                        <HStack spacing={2} align='start'>
                            <Icon as={AiOutlinePhone} color='blue.400' boxSize={4} mt="2px" />
                            <VStack align='start' spacing={0}>
                                <Text fontSize='xs' color='gray.500'>{t('Mobile Phone')}</Text>
                                <Text fontSize='sm' fontWeight='medium'>{parents[level - 1]!.telepon || '-'}</Text>
                            </VStack>
                        </HStack>
                        <HStack spacing={2} align='start'>
                            <Icon as={AiOutlineUser} color='blue.400' boxSize={4} mt="2px" />
                            <VStack align='start' spacing={0}>
                                <Text fontSize='xs' color='gray.500'>{t('Penanggung Jawab')}</Text>
                                <Text fontSize='sm' fontWeight='medium'>
                                    {parents[level - 1]!.id_penanggung_jawab > 0
                                        ? (parentPJ[parents[level - 1]!.id] || '...')
                                        : '-'}
                                </Text>
                            </VStack>
                        </HStack>
                    </SimpleGrid>
                </Box>
            ) : ''}

            <HStack mb={"10px"} justify="space-between">
                <Text fontSize='md' fontWeight={'bold'}>{currentLevelName}</Text>
                {isAdmin ? (
                    <Button size='sm' colorScheme='blue' leftIcon={<Icon as={AiFillPlusCircle} />} onClick={onAdd}>
                        {t('Add')} {currentLevelName}
                    </Button>
                ) : ''}
            </HStack>

            {loading ? (
                <Center py={"20px"}><Spinner size='lg' /></Center>
            ) : (
                <TableContainer>
                    <Table variant='simple' colorScheme='blue' size='sm'>
                        <Thead>
                            <Tr>
                                <Th>{t('Name')}</Th>
                                <Th>{t('Status')}</Th>
                                <Th>{t('E-mail')}</Th>
                                <Th>{t('Mobile Phone')}</Th>
                                {level == 4 ? <Th>{t('jadwal')}</Th> : ''}
                                {level == 4 ? <Th>{t('alamat')}</Th> : ''}
                                <Th>{t('Actions')}</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {items.length === 0 ? (
                                <Tr>
                                    <Td colSpan={level == 4 ? 7 : 5}>
                                        <Center>{t('no_records')}</Center>
                                    </Td>
                                </Tr>
                            ) : (
                                items.map((item) => (
                                    <Tr key={item.id}>
                                        <Td>
                                            {canDrillDown ? (
                                                <Button variant='link' colorScheme='blue' size='sm' onClick={() => drillDown(item)}>
                                                    {item.nama}
                                                </Button>
                                            ) : (
                                                <Button variant='link' colorScheme='blue' size='sm' onClick={() => onView(item)}>
                                                    {item.nama}
                                                </Button>
                                            )}
                                        </Td>
                                        <Td>
                                            <Badge colorScheme={item.status > 0 ? 'green' : 'gray'}>
                                                {item.status > 0 ? t('active') : t('inactive')}
                                            </Badge>
                                        </Td>
                                        <Td>{item.email || '-'}</Td>
                                        <Td>{item.telepon || '-'}</Td>
                                        {level == 4 ? <Td>{item.jadwal || '-'}</Td> : ''}
                                        {level == 4 ? <Td>{item.alamat || '-'}</Td> : ''}
                                        <Td>
                                            <HStack spacing={1}>
                                                {canEditItem(item) ? (
                                                    <Button size='xs' colorScheme='yellow' onClick={() => onEdit(item)}>
                                                        <Icon as={AiFillEdit} />
                                                    </Button>
                                                ) : ''}
                                                {isAdmin ? (
                                                    <Button size='xs' colorScheme='red' onClick={() => onDelete(item)}>
                                                        <Icon as={AiFillDelete} />
                                                    </Button>
                                                ) : ''}
                                                {!canEditItem(item) && !isAdmin ? '-' : ''}
                                            </HStack>
                                        </Td>
                                    </Tr>
                                ))
                            )}
                        </Tbody>
                    </Table>
                </TableContainer>
            )}

            {/* Add/Edit Modal */}
            <Modal isOpen={formIsOpen} onClose={formOnClose}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>{editingId > 0 ? t('Edit') : t('Add')} {currentLevelName}</ModalHeader>
                    <ModalBody>
                        <VStack spacing={3}>
                            <Box w="100%">
                                <Text fontSize='sm' mb="4px">{t('Name')}</Text>
                                <Input size='sm' value={formData.nama} onChange={(e) => setFormData({ ...formData, nama: e.target.value })} maxLength={64} />
                            </Box>
                            <Box w="100%">
                                <Text fontSize='sm' mb="4px">{t('Status')}</Text>
                                <Select size='sm' value={formData.status} onChange={(e) => setFormData({ ...formData, status: +e.target.value })}>
                                    <option value={1}>{t('active')}</option>
                                    <option value={0}>{t('inactive')}</option>
                                </Select>
                            </Box>
                            <Box w="100%">
                                <Text fontSize='sm' mb="4px">{t('E-mail')}</Text>
                                <Input size='sm' type='email' value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} maxLength={64} />
                            </Box>
                            <Box w="100%">
                                <Text fontSize='sm' mb="4px">{t('Mobile Phone')}</Text>
                                <Input size='sm' value={formData.telepon} onChange={(e) => setFormData({ ...formData, telepon: e.target.value })} maxLength={16} />
                            </Box>
                            <Box w="100%">
                                <Text fontSize='sm' mb="4px">{t('Penanggung Jawab')}</Text>
                                {pjSelected ? (
                                    <HStack mb="4px">
                                        <Badge colorScheme='blue'>{pjSelected.nama} ({pjSelected.username})</Badge>
                                    </HStack>
                                ) : ''}
                                <Input size='sm' placeholder={t('Search')} value={pjSearch}
                                    onChange={(e) => { setPjSearch(e.target.value); searchPJ(e.target.value) }} />
                                {pjResults.length > 0 ? (
                                    <VStack align='stretch' maxH='150px' overflowY='auto' borderWidth='1px' borderRadius='md' mt="4px" spacing={0}>
                                        {pjResults.map((p) => (
                                            <Button key={p.id} variant='ghost' size='sm' justifyContent='flex-start' fontWeight='normal'
                                                onClick={() => selectPJ(p)}>
                                                {p.nama} ({p.username})
                                            </Button>
                                        ))}
                                    </VStack>
                                ) : ''}
                            </Box>
                            {level == 4 ? (
                                <>
                                    <Box w="100%">
                                        <Text fontSize='sm' mb="4px">{t('jadwal')}</Text>
                                        <Input size='sm' value={formData.jadwal} onChange={(e) => setFormData({ ...formData, jadwal: e.target.value })} maxLength={128} />
                                    </Box>
                                    <Box w="100%">
                                        <Text fontSize='sm' mb="4px">{t('alamat')}</Text>
                                        <Input size='sm' value={formData.alamat} onChange={(e) => setFormData({ ...formData, alamat: e.target.value })} maxLength={256} />
                                    </Box>
                                </>
                            ) : ''}
                        </VStack>
                    </ModalBody>
                    <ModalFooter>
                        <Button colorScheme='blue' mr={3} size='sm' onClick={submitForm}>{t('Save')}</Button>
                        <Button variant='ghost' size='sm' onClick={formOnClose}>{t('Cancel')}</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Ranting detail modal */}
            <Modal isOpen={viewItem !== null} onClose={() => setViewItem(null)} size='md'>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>
                        <HStack spacing={2} pr={6}>
                            <Icon as={AiFillBank} color='blue.500' boxSize={5} />
                            <Text fontSize='md' fontWeight='bold'>{viewItem?.nama}</Text>
                            <Badge colorScheme='blue' variant='subtle'>{t('Ranting')}</Badge>
                            <Badge colorScheme={viewItem && viewItem.status > 0 ? 'green' : 'gray'} variant='solid'>
                                {viewItem && viewItem.status > 0 ? t('active') : t('inactive')}
                            </Badge>
                        </HStack>
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                            <HStack spacing={2} align='start'>
                                <Icon as={AiOutlineMail} color='blue.400' boxSize={4} mt="2px" />
                                <VStack align='start' spacing={0}>
                                    <Text fontSize='xs' color='gray.500'>{t('E-mail')}</Text>
                                    <Text fontSize='sm' fontWeight='medium'>{viewItem?.email || '-'}</Text>
                                </VStack>
                            </HStack>
                            <HStack spacing={2} align='start'>
                                <Icon as={AiOutlinePhone} color='blue.400' boxSize={4} mt="2px" />
                                <VStack align='start' spacing={0}>
                                    <Text fontSize='xs' color='gray.500'>{t('Mobile Phone')}</Text>
                                    <Text fontSize='sm' fontWeight='medium'>{viewItem?.telepon || '-'}</Text>
                                </VStack>
                            </HStack>
                            <HStack spacing={2} align='start'>
                                <Icon as={AiOutlineUser} color='blue.400' boxSize={4} mt="2px" />
                                <VStack align='start' spacing={0}>
                                    <Text fontSize='xs' color='gray.500'>{t('Penanggung Jawab')}</Text>
                                    <Text fontSize='sm' fontWeight='medium'>
                                        {viewItem && viewItem.id_penanggung_jawab > 0
                                            ? (parentPJ[viewItem.id] || '...')
                                            : '-'}
                                    </Text>
                                </VStack>
                            </HStack>
                            <HStack spacing={2} align='start'>
                                <Icon as={AiOutlineCalendar} color='blue.400' boxSize={4} mt="2px" />
                                <VStack align='start' spacing={0}>
                                    <Text fontSize='xs' color='gray.500'>{t('jadwal')}</Text>
                                    <Text fontSize='sm' fontWeight='medium'>{viewItem?.jadwal || '-'}</Text>
                                </VStack>
                            </HStack>
                            <HStack spacing={2} align='start'>
                                <Icon as={AiOutlineEnvironment} color='blue.400' boxSize={4} mt="2px" />
                                <VStack align='start' spacing={0}>
                                    <Text fontSize='xs' color='gray.500'>{t('alamat')}</Text>
                                    <Text fontSize='sm' fontWeight='medium'>{viewItem?.alamat || '-'}</Text>
                                </VStack>
                            </HStack>
                        </SimpleGrid>
                    </ModalBody>
                    <ModalFooter>
                        <Button variant='ghost' size='sm' onClick={() => setViewItem(null)}>{t('Close')}</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Delete Confirmation */}
            <AlertDialog
                isOpen={deleteTarget !== null}
                leastDestructiveRef={cancelRef}
                onClose={() => { if (!deleting) setDeleteTarget(null) }}
            >
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize='lg' fontWeight='bold'>
                            {t('Delete')} {currentLevelName}
                        </AlertDialogHeader>
                        <AlertDialogBody>
                            {t('confirm_delete')} "{deleteTarget?.nama}"?
                        </AlertDialogBody>
                        <AlertDialogFooter>
                            <Button ref={cancelRef} onClick={() => setDeleteTarget(null)} isDisabled={deleting}>
                                {t('Cancel')}
                            </Button>
                            <Button colorScheme='red' onClick={confirmDelete} ml={3} isLoading={deleting}>
                                {t('Delete')}
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </Box>
    )
}
